import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core'
import {
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { NavLink, Link } from 'react-router-dom'

import { useIsMobile } from '../utils'
import Button from './Button'

const useStyles = makeStyles(theme => ({
  grow: {
    flexGrow: 1,
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    position: 'fixed',
    left: 0,
    right: 0,
    padding: theme.spacing(2, 1),
    // MuiDatatable headers are 100 z-index
    zIndex: 101,
    [theme.breakpoints.up('mobile')]: {
      padding: theme.spacing(2),
    },
  },
  headerInner: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0 auto',
    maxWidth: theme.maxWidth,
  },
  link: {
    color: 'white',
    display: 'block',
    fontFamily: 'Gotham',
    fontWeight: 500,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    position: 'relative',
    textDecoration: 'none',
    textTransform: 'uppercase',
    '&:hover, &.link-active': {
      color: theme.palette.orange.main,
    },
    '&:hover::after, &.link-active::after': {
      content: '""',
      display: 'block',
      width: 8,
      height: 8,
      borderRadius: 7.5,
      backgroundColor: theme.palette.secondary.main,
      position: 'absolute',
      bottom: theme.spacing(-1.5),
      [theme.breakpoints.up('mobile')]: {
        bottom: theme.spacing(-2),
      },
      left: '50%',
      willChange: 'transform',
      transform: 'translate(-50%, -50%)',
    },
  },
  logo: {
    flexShrink: 0,
    height: 45,
    width: 45,
    marginBottom: -10,
    marginTop: -10,
    [theme.breakpoints.up('mobile')]: {
      height: 50,
      width: 186,
      marginBottom: 0,
      marginTop: 0,
    },
  },
  logoImageDesktop: {
    display: 'none',
    height: '100%',
    width: '100%',
    [theme.breakpoints.up('mobile')]: {
      display: 'block',
    },
  },
  logoImageMobile: {
    display: 'block',
    height: '100%',
    width: '100%',
    [theme.breakpoints.up('mobile')]: {
      display: 'none',
    },
  },
  moreIconButton: {
    padding: 0,
  },
  moreNavLink: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
  },
  navIcon: {
    color: 'white',
    cursor: 'pointer',
    height: '1.5rem',
    '&:hover, &.link-active': {
      color: theme.palette.secondary.main,
    },
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.up('mobile')]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  signupButton: {
    // Top/bottom margin to reduce button size on mobile so header fits
    marginBottom: theme.spacing(-0.5),
    marginTop: theme.spacing(-0.5),
    marginLeft: theme.spacing(2),
  },
  signupButtonLink: {
    color: 'inherit',
    textDecoration: 'inherit',
  },
}))

const Header = ({
  homeLoggedOut,
  loggedIn,
  loggedOutLinks,
  moreLinks,
  navLinks,
  onClickNotifications,
  unreadConversations,
  unreadNotifications,
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()

  const [anchorEl, setAnchorEl] = useState(null)
  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const headerLinks = loggedIn ? navLinks : loggedOutLinks
  const logo = (
    <>
      <img
        alt="Tried And Supplied"
        className={classes.logoImageDesktop}
        src="/images/logo.svg"
      />
      <img
        alt="Tried And Supplied"
        className={classes.logoImageMobile}
        src="/images/logo-round.png"
      />
    </>
  )

  return (
    <div className={classes.header}>
      <div className={classes.headerInner}>
        {loggedIn ? (
          <Link to="/" className={classes.logo}>
            {logo}
          </Link>
        ) : (
          <a href={homeLoggedOut} className={classes.logo}>
            {logo}
          </a>
        )}
        <div className={classes.grow} />
        {headerLinks.map(({ exact, href, text, to }, idx) =>
          to ? (
            <NavLink
              activeClassName="link-active"
              exact={exact}
              key={idx}
              to={to}
              className={classes.link}
            >
              {text}
            </NavLink>
          ) : href ? (
            <a key={idx} href={href} className={classes.link}>
              {text}
            </a>
          ) : null
        )}
        {loggedIn ? (
          <>
            <NavLink
              activeClassName="link-active"
              to="/messages"
              className={classes.navIcon}
            >
              <Badge badgeContent={unreadConversations} color="secondary">
                <MessageIcon />
              </Badge>
            </NavLink>
            <div className={classes.navIcon} onClick={onClickNotifications}>
              <Badge badgeContent={unreadNotifications} color="secondary">
                <NotificationsIcon />
              </Badge>
            </div>
            {moreLinks.length ? (
              <>
                <IconButton
                  className={clsx(classes.navIcon, classes.moreIconButton)}
                  onClick={handleOpenMenu}
                  size="small"
                >
                  <SettingsIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  getContentAnchorEl={null}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {moreLinks.map((link, idx) => (
                    <MenuLink
                      className={classes.moreNavLink}
                      key={idx}
                      onCloseMenu={handleCloseMenu}
                      {...link}
                    />
                  ))}
                </Menu>
              </>
            ) : null}
          </>
        ) : (
          <Link className={classes.signupButtonLink} to="/signup">
            <Button
              className={classes.signupButton}
              color="secondary"
              size={isMobile ? 'small' : undefined}
            >
              Sign Up
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

const MenuLink = React.forwardRef(
  ({ className, onClick, onCloseMenu, to, text, ...props }, ref) => {
    const handleClick = () => {
      onClick && onClick()
      onCloseMenu()
    }

    if (onClick) {
      return (
        <MenuItem
          className={className}
          onClick={handleClick}
          ref={ref}
          {...props}
        >
          {text}
        </MenuItem>
      )
    } else {
      return (
        <Link className={className} to={to}>
          <MenuItem onClick={handleClick} ref={ref} {...props}>
            {text}
          </MenuItem>
        </Link>
      )
    }
  }
)

Header.propTypes = {
  homeLoggedOut: PropTypes.string,
  loggedIn: PropTypes.bool,
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      to: PropTypes.string,
    })
  ),
  moreLinks: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func,
      text: PropTypes.string,
      to: PropTypes.string,
    })
  ),
  onClickNotifications: PropTypes.func,
  unreadConversations: PropTypes.number,
  unreadNotifications: PropTypes.number,
}

Header.defaultProps = {
  loggedIn: false,
  loggedOutLinks: [],
  moreLinks: [],
  onClickNotifications: () => {},
  navLinks: [],
}

export default Header
